import { useState } from "react";
import { BellRing, ChevronDown } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { AutoNagSettings } from "./AutoNagSettings";
import {
  createAutoNagSettingsSnapshot,
  normalizeAutoNagSettings,
  validateAutoNagAuthoring,
} from "./auto-nag";
import { autoNagSummary } from "./auto-nag-summary";

export function AutoNagStatusControl({
  value,
  group,
  timeZone,
  votingDeadlineAt = null,
  earliestSlotAt = null,
  nagState = null,
  onSave,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() =>
    createAutoNagSettingsSnapshot(value, { timeZone })
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [editorValid, setEditorValid] = useState(true);
  const normalized = normalizeAutoNagSettings(value);
  const activeSettings = normalized.valid ? normalized.settings : null;

  if (!activeSettings?.enabled) return null;

  const handleOpenChange = (nextOpen) => {
    if (saving) return;
    if (nextOpen) {
      setDraft(createAutoNagSettingsSnapshot(value, { timeZone }));
      setSaveError("");
      setEditorValid(true);
    }
    setOpen(nextOpen);
  };

  const handleSave = async () => {
    if (!editorValid) {
      setSaveError("Finish or correct the highlighted auto-nag setting before saving.");
      return;
    }
    const validation = validateAutoNagAuthoring({
      config: draft,
      votingDeadlineAt,
      earliestSlotAt,
    });
    if (!validation.valid) {
      setSaveError(validation.error || "Choose a valid auto-nag schedule.");
      return;
    }

    setSaving(true);
    setSaveError("");
    try {
      await onSave?.(draft);
      setOpen(false);
    } catch (error) {
      setSaveError(error?.message || "Auto-nag settings could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 shadow-sm shadow-emerald-200/40 transition hover:border-emerald-400 hover:bg-emerald-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 dark:border-emerald-700 dark:bg-emerald-900/55 dark:text-emerald-100 dark:shadow-none dark:hover:border-emerald-600 dark:hover:bg-emerald-900/80"
          aria-label="Auto-nag active — manage settings"
          title="Manage auto-nag settings"
        >
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-50 motion-safe:animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600 dark:bg-emerald-300" />
          </span>
          <BellRing className="h-3.5 w-3.5" aria-hidden="true" />
          <span>Auto-nag active</span>
          <ChevronDown
            className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        className="max-h-[min(38rem,var(--radix-popover-content-available-height))] w-[min(29rem,calc(100vw-1.5rem))] overflow-y-auto p-0"
      >
        <div className="border-b border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 px-4 py-3 dark:border-emerald-800 dark:from-emerald-950/80 dark:to-teal-950/60">
          <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-100">
            <span className="rounded-lg bg-emerald-200/70 p-1.5 dark:bg-emerald-800/70">
              <BellRing className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold">Reminder automation is active</p>
              <p className="mt-0.5 truncate text-[11px] text-emerald-700 dark:text-emerald-200">
                {autoNagSummary(activeSettings)}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4">
          <AutoNagSettings
            group={group}
            value={draft}
            onChange={(next) => {
              setDraft(next);
              setSaveError("");
            }}
            timeZone={timeZone}
            votingDeadlineAt={votingDeadlineAt}
            earliestSlotAt={earliestSlotAt}
            nagState={nagState}
            headingId="poll-page-auto-nag-settings-heading"
            className="border-0 bg-transparent p-0 dark:bg-transparent"
            onValidationChange={setEditorValid}
          />

          {saveError ? (
            <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-200">
              {saveError}
            </p>
          ) : null}

          <div className="sticky bottom-0 z-10 -mx-4 -mb-4 mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-white/95 px-4 pb-4 pt-3 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
            <p className="max-w-[15rem] text-[11px] leading-relaxed text-muted-foreground">
              Manual Nudge returns when automation is off, using the latest nag time for its 8-hour cooldown.
            </p>
            <div className="ml-auto flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                disabled={saving || !editorValid}
                className={draft.enabled === false
                  ? "bg-amber-600 text-white hover:bg-amber-700"
                  : "bg-emerald-700 text-white hover:bg-emerald-800"}
              >
                {saving
                  ? "Saving…"
                  : draft.enabled === false
                    ? "Turn off auto-nag"
                    : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
